import { capitalize } from "@mui/material"
import type { FC } from "react"
import React from "react"
import { Controller, useFormContext } from "react-hook-form"

import { TextInput } from "@/components/Forms"
import LocationSelector from "@/components/Forms/LocationSelector"
import FileArea from "@/components/upload/components/FileArea"
import { DOCUMENT_UPLOAD_MIME_TYPES, DOCUMENT_UPLOAD_TYPES_LABEL } from "@/components/upload/constants"
import { CompanyRegistrationType, FieldGroupEnum, ReferenceTableName } from "@/graphql/codegen/graphql"
import RegistrationTypeSelect from "@/screens/CompanySettings/Registration/components/form/RegistrationTypeSelect"
import type { RegistrationFormValues } from "@/screens/CompanySettings/Registration/forms/registrationSchema"
import { getRegistrationNumberPlaceholder } from "@/screens/CompanySettings/Registration/utils"

interface RegistrationFormProps {
  onSubmit: () => void
  onMarkFileAsNeedRemoval: (fileId: string) => void
  presetRegistrationType?: CompanyRegistrationType
}

const RegistrationForm: FC<RegistrationFormProps> = ({ onSubmit, onMarkFileAsNeedRemoval, presetRegistrationType }) => {
  const methods = useFormContext<RegistrationFormValues>()
  const registrationType = methods.watch("registrationType")
  const companyId = methods.watch("companyId")
  const companyRegistrationId = methods.watch("companyRegistrationId") as string
  const isEditMode = Boolean(companyRegistrationId)

  const isStateRegistration = registrationType === CompanyRegistrationType.State
  const isLocalRegistration = registrationType === CompanyRegistrationType.Local
  const isStateOrLocalRegistration = isStateRegistration || isLocalRegistration

  return (
    <form onSubmit={onSubmit}>
      <RegistrationTypeSelect name='registrationType' disabled={isEditMode || !!presetRegistrationType} />
      {registrationType && (
        <>
          {isStateOrLocalRegistration && <LocationSelector companyId={companyId} name='locationId' />}
          <Controller
            name='registrationNumber'
            control={methods.control}
            render={({ field, fieldState: { error } }) => (
              <TextInput<RegistrationFormValues>
                placeholder={getRegistrationNumberPlaceholder(registrationType)}
                label={getRegistrationNumberPlaceholder(registrationType)}
                field={field}
                required={true}
                error={error}
              />
            )}
          />
          <FileArea<RegistrationFormValues>
            key={registrationType} // Upload area state should be cleared when registration type is changed
            name='registrationDocument'
            title={`${capitalize(registrationType!.toLowerCase())} Registration Document`}
            allowedMimeTypes={DOCUMENT_UPLOAD_MIME_TYPES}
            allowedTypesLabel={DOCUMENT_UPLOAD_TYPES_LABEL}
            referenceId={companyRegistrationId}
            fieldGroups={[FieldGroupEnum.CompanyRegistrationNumber]}
            referenceTableName={ReferenceTableName.CompanyRegistration}
            onMarkFileAsNeedRemoval={onMarkFileAsNeedRemoval}
            showUploadAreaByDefault={false}
          />
        </>
      )}
      {isStateOrLocalRegistration && (
        <>
          <Controller
            name='taxExemptionNumber'
            control={methods.control}
            render={({ field, fieldState: { error } }) => (
              <TextInput<RegistrationFormValues>
                placeholder={getRegistrationNumberPlaceholder()}
                label={`${isStateRegistration ? "State Tax Exempt Number" : "Local Tax Exempt Number"}`}
                field={field}
                error={error}
              />
            )}
          />
          <FileArea<RegistrationFormValues>
            key={registrationType}
            name='taxExemptionDocument'
            title='Tax Exempt Document'
            allowedMimeTypes={DOCUMENT_UPLOAD_MIME_TYPES}
            allowedTypesLabel={DOCUMENT_UPLOAD_TYPES_LABEL}
            referenceId={companyRegistrationId}
            fieldGroups={[FieldGroupEnum.TaxExempt]}
            referenceTableName={ReferenceTableName.CompanyRegistration}
            onMarkFileAsNeedRemoval={onMarkFileAsNeedRemoval}
            showUploadAreaByDefault={false}
          />
        </>
      )}
    </form>
  )
}

export default RegistrationForm
